/**
 * getHTML({ includeStyles }) 결과의 `<style>` 를 저장 본문에 보존하기 위한 재스코프 추출.
 *
 * 문제:
 * - `DOMParser.parseFromString(html, 'text/html')` 는 선두 `<style>` 를 `<head>` 로 올리므로
 *   `doc.body` 만 직렬화하면 `<style>` 가 누락된다.
 * - 최종 저장 HTML 에서는 `.ngw-content` 래퍼가 `wrapEmailBodyHTML` 의 region 재조립 과정에서
 *   제거되므로, 셀렉터의 `.ngw-content` 스코프도 함께 제거해야 저장 본문에서 스타일이 적용된다.
 *
 * 정책:
 * - `.ngw-content` 스코프를 제거하고, **bare element 규칙(예: `p { margin:0 }`, `blockquote {…}`)도
 *   포함**한다. 라이브러리 기본 리셋(특히 `p` 의 margin/padding)이 저장/전송 본문에 보존되도록 하기
 *   위함이다. (과거에는 bare element 를 전부 제외했으나, 그로 인해 `p` 정의가 소실돼 전송 본문의
 *   문단 여백이 편집기와 달라지는 문제가 있었다 — bare element 전체 제외는 해법이 아님.)
 * - 단, `.ngw-content` 제거 후 **선행 combinator(`>`/`+`/`~`)만 남는 규칙**(예:
 *   `.ngw-content > *:not(:first-child)`)은 앵커를 잃어 단독 셀렉터로 유효하지 않으므로 제외한다.
 * - ⚠️ 알려진 트레이드오프: bare `blockquote {…}` 리셋이 외부 인용문(`quoted_body` 내 `<blockquote>`)을
 *   오염시킬 수 있다. 실제 문제가 확인되면 **그 때 blockquote 규칙만 선별 제외**한다(bare element 전체
 *   제외로 되돌리지 않는다).
 * - 동일 셀렉터는 하나로 병합한다.
 */
export function extractVariantStyleCssFromDoc(doc: Document): string {
  const styleEls = Array.from(doc.querySelectorAll('style'))
  if (styleEls.length === 0) return ''
  const rawCss = styleEls.map((s) => s.textContent ?? '').join('\n')
  styleEls.forEach((s) => s.parentElement?.removeChild(s))
  if (rawCss.trim().length === 0 || typeof document === 'undefined') return ''

  // live CSSOM 파싱: 임시 <style> 를 main document 에 부착해 cssRules 를 동기 조회한다.
  // media="not all" 로 실제 적용을 막고, `.ngw-content` 스코프라 라이브 앱에 매칭 요소가 없으며
  // finally 에서 즉시 제거하므로 부작용이 없다.
  const probe = document.createElement('style')
  probe.media = 'not all'
  probe.textContent = rawCss
  document.head.appendChild(probe)
  try {
    const sheet = probe.sheet
    if (sheet == null) return ''
    const merged = new Map<string, string>()
    const order: string[] = []
    for (const rule of Array.from(sheet.cssRules)) {
      if (rule.constructor.name !== 'CSSStyleRule') continue
      let selectorText: string
      let body: string
      try {
        selectorText = (rule as CSSStyleRule).selectorText
        body = (rule as CSSStyleRule).style.cssText
      } catch {
        continue
      }
      if (body.length === 0) continue
      const kept = selectorText
        .split(',')
        .map((s) => s.replace(/\.ngw-content\b\s*/g, '').trim())
        // bare element 포함(정책 참조). 단 `.ngw-content` 제거로 선행 combinator(>/+/~)만 남아
        // 앵커를 잃은 규칙은 단독으로 무효이므로 제외한다.
        .filter((s) => s.length > 0 && !/^[>+~]/.test(s))
      if (kept.length === 0) continue
      const sel = kept.join(', ')
      const prev = merged.get(sel)
      if (prev != null) {
        merged.set(sel, `${prev}${body}`)
      } else {
        merged.set(sel, body)
        order.push(sel)
      }
    }
    return order.map((sel) => `${sel} { ${merged.get(sel)} }`).join('\n')
  } catch {
    return ''
  } finally {
    probe.remove()
  }
}

// 인라인 불가/부적합 셀렉터 판별용 — 가상요소(::before 등)와 상태 의사클래스(:hover 등)는 정적 inline 불가.
const INLINE_INCAPABLE_SELECTOR =
  /::?(?:before|after|first-line|first-letter|selection|placeholder)|:(?:hover|focus|active|visited|target|checked)\b/i

/** CSSStyleDeclaration 을 [prop, value, important][] 로 나열 (jsdom/브라우저 공통 안전 순회). */
function readDeclarations(style: CSSStyleDeclaration): Array<[string, string, boolean]> {
  const out: Array<[string, string, boolean]> = []
  for (let i = 0; i < style.length; i++) {
    const prop = style.item(i)
    out.push([prop, style.getPropertyValue(prop), style.getPropertyPriority(prop) === 'important'])
  }
  return out
}

/** 셀렉터 특이도(간이) — id*100 + (class+attr)*10 + element. 격리된 variant 규칙 정렬용으로 충분. */
function selectorSpecificity(selector: string): number {
  const ids = (selector.match(/#[\w-]+/g) ?? []).length
  const classesAttrs =
    (selector.match(/\.[\w-]+/g) ?? []).length + (selector.match(/\[[^\]]+\]/g) ?? []).length
  const elements = (selector.replace(/[#.][\w-]+/g, ' ').match(/[a-zA-Z][\w-]*/g) ?? []).length
  return ids * 100 + classesAttrs * 10 + elements
}

/**
 * (OMD-39 2단계) `cssText`(발송 재주입 variant CSS)의 **class/id 한정 규칙**을 `root`(nine_body) 요소에
 * **inline `style` 속성으로 전환**하고, inline 불가/부적합 규칙만 **residual CSS 문자열로 반환**한다.
 * 반환된 residual 은 호출부가 `<style>`(→ `wrapEmailBodyHTML` 이 head 로 이동)로 유지한다.
 *
 * Naver 등 `<style>` strip 클라이언트에서 variant 테두리/배경(class 규칙)이 살아남게 하는 것이 목적.
 *
 * 정책:
 * - **bare element 규칙(`p`,`blockquote` 등, `.`/`#` 없음)은 inline 제외 → residual**. 이유: paragraph
 *   노드 parseDOM 이 style 의 margin/padding 을 포착해 재편집 시 attr 로 박히는 taint 회피. base-reset 은
 *   residual `<style>`(head)로 유지되므로 지원 클라이언트에선 그대로 적용된다.
 * - **가상요소/상태 의사클래스는 정적 inline 불가 → residual**.
 * - **`@media`/기타 at-rule 은 residual(조건 그대로 보존)**.
 * - **원본 inline 우선**: 요소에 이미 있는 inline 속성은 덮어쓰지 않는다(quoted 원본·사용자 서식 보존).
 * - **root 스코프 매칭**: `root.querySelectorAll` 로 nine_body 내부만 매칭 → quoted_body 미접촉.
 *
 * 파싱은 `extractVariantStyleCssFromDoc` 와 동일하게 메인 `document.head` probe(`media="not all"`)로 한다
 * (비활성 DOMParser 문서는 `<style>.sheet` 가 null). 매칭·적용은 비활성 `root` 에서 동작(정적 연산).
 * 파싱 실패 등 예외 시 원본 cssText 를 그대로 residual 로 반환(안전 — 기존 `<style>` 유지 동작).
 */
export function inlineVariantCssIntoRoot(cssText: string, root: Element): string {
  if (cssText.trim().length === 0 || typeof document === 'undefined') return ''
  const probe = document.createElement('style')
  probe.media = 'not all'
  probe.textContent = cssText
  document.head.appendChild(probe)
  try {
    const sheet = probe.sheet
    if (sheet == null) return cssText
    const residual: string[] = []
    const perElement = new Map<
      Element,
      Array<{ score: number; decls: Array<[string, string, boolean]> }>
    >()

    for (const rule of Array.from(sheet.cssRules)) {
      const type = rule.constructor.name
      if (type !== 'CSSStyleRule') {
        // @media(조건 보존) 및 기타 at-rule 은 residual 로 그대로 유지.
        residual.push(rule.cssText)
        continue
      }
      const styleRule = rule as CSSStyleRule
      const decls = readDeclarations(styleRule.style)
      if (decls.length === 0) continue
      const body = styleRule.style.cssText
      for (const part of styleRule.selectorText
        .split(',')
        .map((s) => s.trim())
        .filter((s) => s.length > 0)) {
        // 가상요소/상태 의사클래스, 또는 bare element(클래스/id 없음) → residual.
        if (INLINE_INCAPABLE_SELECTOR.test(part) || /[.#]/.test(part) === false) {
          residual.push(`${part} { ${body} }`)
          continue
        }
        let matched: NodeListOf<Element>
        try {
          matched = root.querySelectorAll(part)
        } catch {
          residual.push(`${part} { ${body} }`)
          continue
        }
        const score = selectorSpecificity(part)
        matched.forEach((el) => {
          const list = perElement.get(el) ?? []
          list.push({ score, decls })
          perElement.set(el, list)
        })
      }
    }

    perElement.forEach((rules, el) => {
      const style = (el as HTMLElement).style
      const original = new Set<string>()
      for (let i = 0; i < style.length; i++) original.add(style.item(i))
      rules.sort((a, b) => a.score - b.score) // 낮은 특이도 먼저 → 높은 특이도가 덮어씀
      for (const { decls } of rules) {
        for (const [prop, value, important] of decls) {
          if (original.has(prop)) continue // 원본 inline 우선(덮어쓰지 않음)
          style.setProperty(prop, value, important ? 'important' : '')
        }
      }
    })

    return residual.join('\n')
  } catch {
    return cssText
  } finally {
    probe.remove()
  }
}
